'use client';

import React, { useState, useEffect, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, EffectFade, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';
import 'swiper/css/navigation';
import { motion } from 'framer-motion';
import { FaPhone, FaWhatsapp, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const AUTOPLAY_DELAY = 3500;
const BRAND_BLUE = "#3b82f6";

const slides = [
  {
    image: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=1400&q=80',
    alt: 'Skilled construction workers and manpower supply in Doha Qatar',
    badge: "Qatar's #1 Manpower Supplier",
    title: (
      <>
        Build with <span style={{ color: BRAND_BLUE }}>Total Confidence</span>
      </>
    ),
    subtitle: 'Professional construction manpower supply in Qatar, supporting civil, MEP, and finishing works across major projects in Doha and Lusail.',
  },
  {
    image: 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&w=1400&q=80',
    alt: 'MEP and civil works contractors in Qatar',
    badge: 'Certified Technical Staff',
    title: (
      <>
        From Ground work To <span style={{ color: BRAND_BLUE }}>Completion</span>
      </>
    ),
    subtitle: 'Integrated subcontracting teams for civil construction, MEP services, finishing trades, waterproofing, and ongoing site maintenance.',
  },
  {
    image: 'https://images.unsplash.com/photo-1489515217757-5fd1be406fef?auto=format&fit=crop&w=1400&q=80',
    alt: 'Fast labor mobilization for Qatar construction sites',
    badge: '24/7 Labor Mobilization',
    title: (
      <>
       Skilled Manpower  <span style={{ color: BRAND_BLUE }}>On Demand</span>
      </>
    ),
    subtitle: 'Reliable manpower deployment across Qatar with trained labor, technicians, and supervisors mobilized to meet project schedules.',
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.2 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const NewHeroSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const prevRef = useRef(null);
  const nextRef = useRef(null);

  useEffect(() => {
    setProgress(0);
    let start;
    let frame;
    const animate = (t) => {
      if (!start) start = t;
      const elapsed = t - start;
      const pct = Math.min((elapsed / AUTOPLAY_DELAY) * 100, 100);
      setProgress(pct);
      if (pct < 100) frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [activeIndex]);

  return (
    <section 
      style={{ position: 'relative', height: '100svh', minHeight: '550px', width: '100%', overflow: 'hidden', background: '#000' }}
      aria-label="Welcome to Abraj Al Dhafna - Construction Manpower Supply Qatar"
    >
      {/* Dynamic SEO & Responsive Style Block */}
      <style dangerouslySetInnerHTML={{ __html: `
        .hero-card { padding: 2rem !important; gap: 1rem !important; width: 95% !important; margin: 0 10px; }
        .hero-title { font-size: 2.2rem !important; }
        .hero-btns { width: 100%; }
        .hero-btn { width: 100% !important; padding: 0.9rem !important; }
        .nav-controls { display: none !important; }

        @media (min-width: 768px) {
          .hero-card { padding: 3.5rem !important; gap: 1.5rem !important; width: 85% !important; }
          .hero-title { font-size: 3.2rem !important; }
          .hero-btns { width: auto; }
          .hero-btn { width: auto !important; padding: 1rem 2rem !important; }
          .nav-controls { display: flex !important; }
        }
        @media (min-width: 1024px) {
          .hero-title { font-size: 4rem !important; }
          .hero-card { width: 90% !important; max-width: 850px !important; }
        }
      `}} />

      <Swiper
        modules={[Autoplay, Pagination, EffectFade, Navigation]}
        effect="fade"
        loop
        autoplay={{ delay: AUTOPLAY_DELAY, disableOnInteraction: false }}
        navigation={{ prevEl: prevRef.current, nextEl: nextRef.current }}
        onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
        style={{ width: '100%', height: '100%' }}
      >
        {slides.map((slide, idx) => (
          <SwiperSlide key={idx}>
            <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              
              {/* Background with Alt Text for SEO */}
              <motion.div
                role="img"
                aria-label={slide.alt}
                animate={activeIndex === idx ? { scale: 1.08 } : { scale: 1 }}
                transition={{ duration: 4, ease: 'linear' }}
                style={{
                  position: 'absolute', inset: 0,
                  backgroundImage: `url(${slide.image})`,
                  backgroundSize: 'cover', backgroundPosition: 'center',
                }}
              />
              
              {/* Overlay for Readability */}
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.85) 100%)', zIndex: 1 }} />

              {activeIndex === idx && (
                <motion.div
                  className="hero-card"
                  style={{
                    position: 'relative', zIndex: 10, color: '#ffffff',
                    borderRadius: '2rem', background: 'rgba(56, 62, 68, 0.55)',
                    border: '1px solid rgba(255, 255, 255, 0.1)', backdropFilter: 'blur(16px)',
                    display: 'flex', flexDirection: 'column',
                  }}
                  variants={containerVariants}
                  initial="hidden"
                  animate="show"
                >
                  <motion.div style={badgeStyle} variants={itemVariants}>
                    <span style={dotStyle} /> {slide.badge}
                  </motion.div>

                  {/* H1 for the active slide is critical for SEO */}
                  <motion.h1 className="hero-title" style={{ fontWeight: 900, lineHeight: 1.1, margin: 0, letterSpacing: '-1px' }} variants={itemVariants}>
                    {slide.title}
                  </motion.h1>

                  <motion.p style={{ fontSize: '1.1rem', opacity: 0.9, maxWidth: '600px', lineHeight: 1.6, margin: 0 }} variants={itemVariants}>
                    {slide.subtitle}
                  </motion.p>

                  <motion.nav 
                    className="hero-btns" 
                    style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '0.5rem' }} 
                    variants={itemVariants}
                  >
                    <motion.button 
                      whileHover={{ y: -3 }}
                      whileTap={{ scale: 0.97 }} 
                      className="hero-btn" 
                      style={primaryBtn}
                      onClick={() => window.open('tel:+97444170859', '_self')}
                      title="Call Abraj Al Dhafna for Manpower Services"
                    >
                      <FaPhone aria-hidden="true" /> Call Now
                    </motion.button>
                    
                    <motion.button 
                      whileHover={{ y: -3 }}
                      whileTap={{ scale: 0.97 }} 
                      className="hero-btn" 
                      style={secondaryBtn}
                      onClick={() => window.open('https://wa.me/97455994434', '_blank')}
                      title="Contact us on WhatsApp for skilled labor supply"
                    >
                      <FaWhatsapp color="#22c55e" aria-hidden="true" /> WhatsApp
                    </motion.button>
                  </motion.nav>
                </motion.div>
              )}

              {/* Autoplay Progress Line */}
              {activeIndex === idx && (
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '4px', background: 'rgba(255,255,255,0.1)', zIndex: 20 }}>
                  <div style={{ height: '100%', width: `${progress}%`, background: BRAND_BLUE, transition: 'width 0.2s linear' }} />
                </div>
              )}
            </div>
          </SwiperSlide>
        ))}
        
        {/* Navigation - Hidden on Mobile to prevent overlap */}
        <div className="nav-controls" style={{ position: 'absolute', bottom: '40px', right: '40px', zIndex: 30, gap: '12px' }}>
          <button ref={prevRef} style={navBtnStyle} aria-label="Previous Slide"><FaChevronLeft size={14} /></button>
          <button ref={nextRef} style={navBtnStyle} aria-label="Next Slide"><FaChevronRight size={14} /></button>
        </div>
      </Swiper>
    </section>
  );
};

// Internal Styles
const badgeStyle = { display: 'inline-flex', alignItems: 'center', gap: '0.6rem', alignSelf: 'flex-start', padding: '0.5rem 1rem', borderRadius: '12px', background: `${BRAND_BLUE}20`, border: `1px solid ${BRAND_BLUE}40`, color: '#fff', fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' };
const dotStyle = { width: '8px', height: '8px', borderRadius: '50%', background: BRAND_BLUE, boxShadow: `0 0 10px ${BRAND_BLUE}` };
const primaryBtn = { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.7rem', borderRadius: '14px', border: 'none', cursor: 'pointer', fontWeight: 700, background: BRAND_BLUE, color: '#fff', fontSize: '0.9rem', boxShadow: `0 10px 20px ${BRAND_BLUE}30`, transition: '0.3s' };
const secondaryBtn = { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.7rem', borderRadius: '14px', border: '1px solid rgba(255,255,255,0.2)', cursor: 'pointer', fontWeight: 700, background: 'rgba(255,255,255,0.08)', color: '#fff', fontSize: '0.9rem', transition: '0.3s' };
const navBtnStyle = { width: '48px', height: '48px', borderRadius: '14px', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(15, 23, 42, 0.8)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: '0.3s' };

export default NewHeroSection;